import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/contexts/ThemeContext";
import { ChatMessage } from "@/components/ChatMessage";
import { Lock, Share2, MessageCircle, ExternalLink, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import oreogImg from "/src/assets/image.png";
import { cn } from "@/lib/utils";

interface DBMessage {
  id: string;
  content: string;
  role: string;
  model: string | null;
  created_at: string;
  message_order: number;
  metadata?: any;
  attached_media_url?: string;
  attached_media_type?: string;
}

interface Message {
  id: number;
  content: string;
  type: "user" | "ai";
  timestamp: Date;
  model: string;
  displayModel?: string;
}

interface SharedSession {
  id: string;
  title: string;
  created_at: string;
  share_date: string;
  user_id: string;
}

export default function Shared() {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme } = useTheme();
  const isDarkMode =
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  const [messages, setMessages] = useState<Message[]>([]);
  const [session, setSession] = useState<SharedSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Ensure shared chats are only opened on the production domain
  useEffect(() => {
    if (!shareId) return;
    const prod = "https://oreog.netlify.app";
    if (window.location.origin !== prod) {
      window.location.href = `${prod}/s/${shareId}`;
    }
  }, [shareId]);

  useEffect(() => {
    if (shareId) {
      loadSharedChat();
    }
  }, [shareId]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsAuthenticated(!!user);
    const verified = !!(user && ((user as any).email_confirmed_at || (user as any).user_metadata?.email_verified));
    setIsVerified(verified);
  };

  const loadSharedChat = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First, validate the share link and get session info
      const { data: sessionData, error: sessionError } = await supabase
        .from("chat_sessions")
        .select("id, title, created_at, share_date, user_id")
        .eq("shared_link", shareId)
        .eq("is_shared", true)
        .single();

      if (sessionError || !sessionData) {
        setError("This chat link is invalid or has been unshared.");
        setIsLoading(false);
        return;
      }

      setSession(sessionData);

      // Get all messages from this session that existed at the time of sharing
      // We filter by messages created before or at share_date
      const { data: messagesData, error: messagesError } = await supabase
        .from("chat_messages")
        .select("id, content, role, model, created_at, message_order, metadata, attached_media_url, attached_media_type")
        .eq("session_id", sessionData.id)
        .lte("created_at", sessionData.share_date)
        .order("message_order", { ascending: true });

      if (messagesError) {
        throw messagesError;
      }

      // Transform DB messages to component format
      // Note: We don't include attachedMedia since it requires File objects
      // The ChatMessage component will handle missing media gracefully
      const transformedMessages: Message[] = (messagesData || []).map((msg: DBMessage, index) => ({
        id: index,
        content: msg.content,
        type: msg.role === "user" ? "user" : "ai",
        timestamp: new Date(msg.created_at),
        model: msg.model || "ai",
      }));

      setMessages(transformedMessages);
    } catch (err) {
      console.error("Error loading shared chat:", err);
      setError("Failed to load shared chat. Please try again.");
      toast({
        title: "Error",
        description: "Failed to load shared chat",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please log in to send messages",
      });
      navigate("/");
      return;
    }

    if (!isVerified) {
      toast({
        title: "Verification Required",
        description: "Please verify your account to send messages",
      });
      navigate("/");
      return;
    }

    // Navigate to main chat with the message
    navigate("/", {
      state: {
        initialMessage: inputValue,
        fromShared: true,
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  if (isLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-[#303030]" : "bg-gray-50"
        }`}
      >
        <div className="flex flex-col items-center gap-4">
          <img 
            src={oreogImg} 
            alt="Loading..." 
            className="h-24 rounded-xl animate-pulse"
          />
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 md:p-6 ${
          isDarkMode ? "bg-[#303030]" : "bg-gray-50"
        }`}
      >
        <div
          className={`max-w-md w-full rounded-2xl border p-8 text-center ${
            isDarkMode
              ? "bg-[#1c1c1c] border-[#2e2e2e]"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="mb-4">
            <Lock
              className={`w-16 h-16 mx-auto ${
                isDarkMode ? "text-gray-600" : "text-gray-400"
              }`}
            />
          </div>
          <h1
            className={`text-2xl font-bold mb-2 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Chat Not Found
          </h1>
          <p
            className={`mb-6 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {error ||
              "This chat link is invalid, expired, or has been unshared."}
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Go to OREOG
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${isDarkMode ? "bg-[#303030]" : "bg-gray-50"}`}
    >
      {/* Header */}
      <header
        className={`sticky top-1 z-50 bg-transparent`}
      >
        <div className={`max-w-4xl mx-auto py-2 px-3 rounded-full shadow-lg ${isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'}`}>
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <img src={oreogImg} alt="OREOG" className="h-6 sm:h-8 rounded-lg flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h1
                  className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {session.title}
                </h1>
                <p
                  className={`text-[10px] sm:text-xs flex items-center gap-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-600"
                  }`}
                >
                  <span className="truncate">Shared on {formatDate(session.share_date)}</span>
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              size="sm"
              className={`flex-shrink-0 rounded-2xl ${
                isDarkMode
                  ? "border-[#2e2e2e] hover:bg-[#1c1c1c]"
                  : "border-gray-300"
              }`}
            >
              <ExternalLink className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Try OREOG</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="max-w-4xl mx-auto px-3 sm:px-4 pt-6 sm:pt-8 pb-28">

        {/* Messages List */}
        {messages.length > 0 ? (
          <div className="space-y-6">
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                isDarkMode={isDarkMode}
                isStreaming={false}
                onShare={() => {}}
                hideActions={true}
              />
            ))}
          </div>
        ) : (
          <div
            className={`text-center py-12 ${
              isDarkMode ? "text-gray-500" : "text-gray-600"
            }`}
          >
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No messages in this shared chat.</p>
          </div>
        )}
      </main>

      {/* Input Area */}
      <div className={`fixed bottom-0 left-0 right-0 z-50`}>
        <div className="max-w-4xl mx-auto px-3 sm:px-4 pt-4 pb-2 bg-transparent">
          <div
            className={cn(
              'relative shadow-lg transition-all duration-200 overflow-hidden rounded-2xl',
              isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
            )}
          >
            <div className="flex gap-2 px-2 sm:px-4 py-2 items-end">
              {/* Input */}
              <div className="flex-1 min-w-0">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about this chat..."
                  rows={1}
                  className={cn(
                    'w-full resize-none border-none outline-none bg-transparent text-base break-words',
                    isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400',
                    'scrollbar-hide overflow-y-auto'
                  )}
                  style={{ maxHeight: '200px', wordBreak: 'break-word', overflowWrap: 'break-word' }}
                />
              </div>

              {/* Send Button (always visible, disabled without text) */}
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className={cn(
                  'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200',
                  isDarkMode
                    ? 'bg-white text-black hover:bg-white/90'
                    : 'bg-black text-white hover:bg-gray-800',
                  !inputValue.trim() && 'opacity-50 cursor-not-allowed'
                )}
                aria-disabled={!inputValue.trim()}
                title={!inputValue.trim() ? 'Type a message to send' : 'Send'}
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
